import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Select, StatusPip, Table, Tabs } from '../components/ui';
import { ConfirmDialog, EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import { ApiError } from '../api/client';
import { listDevices, subscribeDevices, type DeviceRecord } from '../api/devices';
import {
  deploy,
  listDeployments,
  listPublishedLayouts,
  rollback,
  subscribeDeployments,
  type DeploymentRecord,
  type LayoutRecord
} from '../api/deployments';
import { relativeTime, statusPresentation } from '../features/devices/deviceView';

type Tab = 'Deploy' | 'History' | 'Rollout';

function capabilityReasons(error: unknown): string {
  if (error instanceof ApiError && Array.isArray(error.details)) {
    return error.details.map((reason) => String(reason)).join('; ');
  }
  return error instanceof Error ? error.message : 'Deployment failed.';
}

export function Deployments() {
  const { session } = useSession();
  const { notify } = useToast();
  const writable = (session?.role ?? 'viewer') !== 'viewer';

  const [tab, setTab] = useState<Tab>('Deploy');
  const [layouts, setLayouts] = useState<LayoutRecord[]>([]);
  const [devices, setDevices] = useState<DeviceRecord[]>([]);
  const [deployments, setDeployments] = useState<DeploymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLayout, setSelectedLayout] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [results, setResults] = useState<Array<{ device: string; ok: boolean; message: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<DeploymentRecord | null>(null);

  const load = useCallback(async () => {
    try {
      const [layoutList, deviceList, deploymentList] = await Promise.all([
        listPublishedLayouts(),
        listDevices(),
        listDeployments()
      ]);
      setLayouts(layoutList);
      setDevices(deviceList);
      setDeployments(deploymentList);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load deployments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => subscribeDevices(setDevices), []);
  useEffect(() => subscribeDeployments(() => void load()), [load]);

  const layoutName = (id: string | undefined) => layouts.find((layout) => layout.id === id)?.name ?? id ?? 'unknown';

  const activeDeploymentByDevice = useMemo(() => {
    const map = new Map<string, DeploymentRecord>();
    for (const deployment of deployments) {
      const deviceId = deployment.device ?? '';
      const existing = map.get(deviceId);
      if (!existing || Number(deployment.version ?? 0) > Number(existing.version ?? 0)) map.set(deviceId, deployment);
    }
    return map;
  }, [deployments]);

  const toggleDevice = (id: string) =>
    setSelectedDeviceIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  async function runDeploy() {
    if (!selectedLayout || selectedDeviceIds.length === 0) return;
    setBusy(true);
    const outcome: Array<{ device: string; ok: boolean; message: string }> = [];
    for (const deviceId of selectedDeviceIds) {
      const device = devices.find((item) => item.id === deviceId);
      const name = device?.name ?? deviceId;
      try {
        const result = await deploy(deviceId, selectedLayout);
        outcome.push({ device: name, ok: true, message: `v${result.version} deployed` });
      } catch (cause) {
        outcome.push({ device: name, ok: false, message: capabilityReasons(cause) });
      }
    }
    setResults(outcome);
    setBusy(false);
    notify(`Deploy finished for ${outcome.filter((item) => item.ok).length}/${outcome.length} device(s)`);
    await load();
  }

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / DEPLOYMENTS</span>
          <h1>Deployments</h1>
        </div>
      </header>

      <Tabs items={['Deploy', 'History', 'Rollout']} value={tab} onChange={(value) => setTab(value as Tab)} />

      {loading && <LoadingState label="Loading deployments" />}
      {error && <ErrorState title="Deployments unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && tab === 'Deploy' && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">NEW DEPLOYMENT</span>
          </header>
          <div className="panel-body">
            {!writable && <p className="detail-note">Viewers cannot deploy.</p>}
            <label className="index-label" htmlFor="layout-select">
              PUBLISHED LAYOUT
            </label>
            <Select
              id="layout-select"
              value={selectedLayout}
              onChange={(event) => setSelectedLayout(event.target.value)}
              disabled={!writable}
            >
              <option value="">Choose a layout</option>
              {layouts.map((layout) => (
                <option key={layout.id} value={layout.id}>
                  {layout.name ?? layout.id} (v{layout.version ?? 0})
                </option>
              ))}
            </Select>
            {layouts.length === 0 && <p className="detail-note">No published layouts yet.</p>}

            <div className="deploy-devices">
              {devices.map((device) => {
                const status = statusPresentation(device.status);
                return (
                  <label key={device.id} className="deploy-device-row">
                    <input
                      type="checkbox"
                      checked={selectedDeviceIds.includes(device.id)}
                      onChange={() => toggleDevice(device.id)}
                      disabled={!writable || device.status === 'revoked'}
                    />
                    <span>{device.name ?? device.id}</span>
                    <StatusPip status={status.pip} label={status.label} />
                    <span className="mono">{device.capabilities?.device_class ?? 'unknown'}</span>
                  </label>
                );
              })}
              {devices.length === 0 && <EmptyStateBlock title="No devices" detail="Claim a device first." />}
            </div>

            <div className="deploy-review">
              <strong>Review</strong>
              <p>
                {selectedLayout ? layoutName(selectedLayout) : 'No layout selected'} → {selectedDeviceIds.length} device(s)
              </p>
              <Button type="button" disabled={!writable || busy || !selectedLayout || selectedDeviceIds.length === 0} onClick={() => void runDeploy()}>
                {busy ? 'Deploying…' : 'Deploy'}
              </Button>
            </div>

            {results.length > 0 && (
              <ul className="audit-list">
                {results.map((result) => (
                  <li key={result.device}>
                    <Badge>{result.ok ? 'ok' : 'rejected'}</Badge> {result.device} — {result.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {!loading && !error && tab === 'History' && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">DEPLOYMENT HISTORY</span>
          </header>
          {deployments.length === 0 ? (
            <div className="panel-body">
              <EmptyStateBlock title="No deployments" detail="Deployments appear here once issued." />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Layout</th>
                  <th>Layout ver</th>
                  <th>Deployment ver</th>
                  <th>Status</th>
                  <th>When</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {deployments.map((deployment) => (
                  <tr key={deployment.id}>
                    <td>{deployment.expand?.device?.name ?? deployment.device ?? '—'}</td>
                    <td>{deployment.expand?.layout?.name ?? deployment.layout ?? '—'}</td>
                    <td className="mono">v{deployment.layout_version ?? 0}</td>
                    <td className="mono">v{deployment.version ?? 0}</td>
                    <td>
                      <Badge>{deployment.status ?? 'unknown'}</Badge>
                    </td>
                    <td className="mono">{relativeTime(deployment.deployed_at)}</td>
                    <td>
                      {writable && deployment.status === 'active' && (
                        <Button variant="quiet" type="button" onClick={() => setRollbackTarget(deployment)}>
                          Roll back
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      {!loading && !error && tab === 'Rollout' && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">ROLLOUT STATUS</span>
            <span>config version / device</span>
          </header>
          <Table>
            <thead>
              <tr>
                <th>Device</th>
                <th>Status</th>
                <th>Config version</th>
                <th>Current layout</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => {
                const status = statusPresentation(device.status);
                const active = activeDeploymentByDevice.get(device.id);
                const fetched = active && device.current_deployment === active.id;
                return (
                  <tr key={device.id}>
                    <td>{device.name ?? device.id}</td>
                    <td>
                      <StatusPip status={status.pip} label={status.label} />
                    </td>
                    <td className="mono">
                      v{device.config_version ?? 0} {fetched ? '(fetched)' : '(pending)'}
                    </td>
                    <td>{active ? `${layoutName(active.layout)} v${active.layout_version ?? 0}` : 'none'}</td>
                    <td className="mono">{relativeTime(device.last_seen)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </section>
      )}

      <ConfirmDialog
        open={Boolean(rollbackTarget)}
        title="Roll back deployment"
        confirmWord="rollback"
        detail={
          rollbackTarget
            ? `Restore the previous deployment for ${rollbackTarget.expand?.device?.name ?? rollbackTarget.device ?? 'this device'}.`
            : ''
        }
        confirmLabel="Roll back"
        onClose={() => setRollbackTarget(null)}
        onConfirm={() => {
          const target = rollbackTarget;
          setRollbackTarget(null);
          if (!target) return;
          void rollback(target.id)
            .then((result) => {
              notify(`Rolled back to v${result.version}`);
              void load();
            })
            .catch((cause) => notify(capabilityReasons(cause)));
        }}
      />
    </main>
  );
}
